/* eslint-disable react/no-multi-comp */
// Wrapper around lego Combobox that consumes a SnowflakeJobs filter facet
// config (DROPDOWN-static or API_DROPDOWN-paginated) and renders it uniformly.
//
// Used outside the DataTable (e.g. CustomWorkloadsGraph "Filter Projects/Apps")
// where we can't rely on the table's built-in FilterInput. For API_DROPDOWN
// the wrapper accumulates pages via onLoadMore and stabilizes the callback
// identity so the lego Combobox's mount-effect doesn't re-fire and clobber
// active searches.

import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Combobox, ComboboxOption } from "@altimateai/lego";
import { FilterType } from "@/components/tables/types";
import {
  ApiDropdownFacetConfig,
  FacetConfig,
} from "@/pages/query/utils/snowflakeJobsFilterConfig";

interface FacetComboboxProps {
  facet: FacetConfig;
  value: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  header: string;
  searchPlaceholder: string;
}

const StaticFacetCombobox: FC<
  Omit<FacetComboboxProps, "facet"> & { options: ComboboxOption[] }
> = ({ options, value, onChange, placeholder, header, searchPlaceholder }) => (
  <Combobox
    options={options}
    value={value}
    onChange={v => {
      if (Array.isArray(v)) onChange(v);
    }}
    placeholder={placeholder}
    header={header}
    searchPlaceholder={searchPlaceholder}
    multiSelect
    showApplyButton
    showClearButton
    popoverContentProps={{ className: "al-min-w-[200px]" }}
  />
);

// `seeMore` is part of ApiDropdownFacetConfig (consumed by the table-side
// FilterInput) but lego Combobox does its own pagination via onLoadMore,
// so we don't forward it here.
const ApiFacetCombobox: FC<
  Omit<FacetComboboxProps, "facet"> & Omit<ApiDropdownFacetConfig, "seeMore">
> = ({
  apiFunction,
  extractorFunction,
  pageSize,
  facetId,
  value,
  onChange,
  placeholder,
  header,
  searchPlaceholder,
}) => {
  const [options, setOptions] = useState<ComboboxOption[]>([]);
  const [hasMore, setHasMore] = useState(false);
  // Lego Combobox fires onLoadMore("", 1) from its mount effect — gate it
  // behind the popover's open state so we only hit the network when the
  // user actually opens the dropdown (avoids eager prefetch on every page
  // mount that has a FacetCombobox in the tree).
  const [hasOpened, setHasOpened] = useState(false);

  // Lego Combobox re-runs its mount effect (which calls onLoadMore("", 1))
  // whenever the callback identity changes — keep apiFunction/extractor in
  // refs so onLoadMore stays stable across re-renders.
  //
  // Sync the refs DURING RENDER (not in useEffect) because the lego
  // Combobox is remounted via key={facetId} on facet swaps, and React
  // runs child mount effects BEFORE the parent's effects. A useEffect-
  // based ref sync would leave apiRef pointing at the previous facet's
  // closure when lego's mount effect calls onLoadMore — the request
  // would go to the wrong endpoint (and the new facet would never load).
  const apiRef = useRef(apiFunction);
  const extractorRef = useRef(extractorFunction);
  apiRef.current = apiFunction;
  extractorRef.current = extractorFunction;

  // Apply each (search, page) response exactly once. Lego Combobox calls
  // onLoadMore from both its mount effect and its handleSearch path; without
  // a guard we'd re-append the same page when both fire in quick succession.
  const appliedKeyRef = useRef<string>("");

  // Bumped on facet swap so an in-flight response from the previous facet
  // can't write into the new facet's options (race protection).
  const generationRef = useRef(0);

  const handleLoadMore = useCallback(
    async (searchValue?: string, requestedPage?: number): Promise<void> => {
      const search = searchValue ?? "";
      const page = requestedPage ?? 1;
      const key = `${search}|${page}`;
      if (appliedKeyRef.current === key) return;
      appliedKeyRef.current = key;
      const generation = generationRef.current;

      let data: unknown;
      try {
        data = await apiRef.current({ prefix: search, page, size: pageSize });
      } catch {
        // Clear the dedupe key so the user can retry the same (search, page).
        if (appliedKeyRef.current === key) appliedKeyRef.current = "";
        return;
      }

      // Drop stale responses (facet swapped while we were awaiting).
      if (generation !== generationRef.current) return;

      const items = extractorRef.current(data).map(item => ({
        label: typeof item.label === "string" ? item.label : item.value,
        value: item.value,
      }));
      const resp = data as { total_pages?: number; total?: number } | undefined;
      const totalPages = resp?.total_pages ?? 1;

      if (page === 1) {
        setOptions(items);
      } else {
        setOptions(prev => [...prev, ...items]);
      }
      setHasMore(page < totalPages);
    },
    [pageSize]
  );

  // When the underlying facet swaps (e.g. graph groupBy changes from
  // project_id to app_id, replacing /teams with /apps) reset accumulated
  // state AND hasOpened. The hasOpened reset is what keeps the swap
  // lazy: the lego Combobox is remounted via key={facetId} below, so
  // its mount effect would fire onLoadMore immediately if hasOpened
  // were still true from the previous facet's interaction. Resetting
  // it means onLoadMore is undefined on the fresh mount → no fetch
  // until the user actually opens the dropdown again.
  useEffect(() => {
    setOptions([]);
    setHasMore(false);
    setHasOpened(false);
    appliedKeyRef.current = "";
    generationRef.current += 1;
  }, [facetId]);

  // No-op: lego Combobox fires onPageReset on every keystroke before
  // onLoadMore; resetting parent state here would race with the search.
  const handlePageReset = useCallback(() => {
    appliedKeyRef.current = "";
  }, []);

  return (
    <Combobox
      // Remount on facet swap so lego's mount effect re-fires onLoadMore
      // and its internal isLoading flag drives the "Loading..." state in
      // CommandEmpty during the in-flight fetch.
      key={facetId}
      options={options}
      value={value}
      onChange={v => {
        if (Array.isArray(v)) onChange(v);
      }}
      placeholder={placeholder}
      header={header}
      searchPlaceholder={searchPlaceholder}
      multiSelect
      showApplyButton
      showClearButton
      onOpenChange={open => {
        if (open) setHasOpened(true);
      }}
      onLoadMore={hasOpened ? handleLoadMore : undefined}
      hasMore={hasMore}
      onPageReset={handlePageReset}
      popoverContentProps={{ className: "al-min-w-[200px]" }}
    />
  );
};

export const FacetCombobox: FC<FacetComboboxProps> = ({ facet, ...rest }) => {
  if (facet.filterType === FilterType.API_DROPDOWN) {
    return (
      <ApiFacetCombobox
        {...rest}
        apiFunction={facet.apiFunction}
        extractorFunction={facet.extractorFunction}
        pageSize={facet.pageSize}
        facetId={facet.facetId}
        filterType={facet.filterType}
      />
    );
  }
  return <StaticFacetCombobox {...rest} options={facet.options} />;
};
